import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { customerApi, inventoryApi, pastOrderApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import Button from '../../components/common/Button'

/**
 * Import Past Orders — temporary admin tool for backfilling Anvil-era orders.
 *
 * Workflow:
 *   1. Pick the customer (home group) the PDFs belong to
 *   2. Pick an import mode:
 *        - "Historical (picked up)"   → status=picked_up. For pre-cutover orders
 *                                       that were already paid and picked up.
 *        - "Active (pending payment)" → status=pending_payment. For pre-cutover
 *                                       orders that still need to flow through
 *                                       the normal pay/pack/pickup workflow.
 *   3. Drop in or select all PDFs for that customer
 *   4. Click "Preview" — server parses each PDF and returns a preview table
 *   5. Expand any row to inspect/edit the parsed items (pick from inventory
 *      dropdown, fix typos, add/delete lines)
 *   6. Review each row, uncheck any to skip, then click "Import"
 *   7. Done — orders appear under /admin/orders for that customer.
 *       - Historical: marked Picked Up
 *       - Active:     listed under /admin/pending-payments
 */
export default function ImportPastOrders() {
    const { isSysAdmin } = useAuth()

    const [customers, setCustomers] = useState([])
    const [customerId, setCustomerId] = useState('')
    const [importMode, setImportMode] = useState('historical')
    const [files, setFiles] = useState([])
    const [preview, setPreview] = useState(null)
    const [previewing, setPreviewing] = useState(false)
    const [committing, setCommitting] = useState(false)
    const [selectedRows, setSelectedRows] = useState({})
    const [expandedRows, setExpandedRows] = useState({})
    const [result, setResult] = useState(null)
    const [inventory, setInventory] = useState([])

    useEffect(() => {
        loadCustomers()
        loadInventory()
    }, [])

    async function loadCustomers() {
        try {
            const response = await customerApi.list()
            if (response.success) setCustomers(response.customers || [])
        } catch {
            toast.error('Could not load customers')
        }
    }

    async function loadInventory() {
        try {
            const response = await inventoryApi.list()
            if (response.success) setInventory(response.items || [])
        } catch {
            // non-fatal — picker just won't have options
        }
    }

    // Build a quick-lookup map by item_number for live re-match as user types
    const inventoryByItemNumber = useMemo(() => {
        const map = new Map()
        for (const item of inventory) {
            const key = (item.item_number || '').trim().toUpperCase()
            if (key && !map.has(key)) map.set(key, item)
        }
        return map
    }, [inventory])

    function handleFiles(fileList) {
        const arr = Array.from(fileList).filter(f =>
            f.name.toLowerCase().endsWith('.pdf')
        )
        setFiles(arr)
        setPreview(null)
        setSelectedRows({})
        setExpandedRows({})
        setResult(null)
    }

    async function handlePreview() {
        if (!customerId) {
            toast.error('Pick a home group first')
            return
        }
        if (files.length === 0) {
            toast.error('Add some PDFs first')
            return
        }
        setPreviewing(true)
        setResult(null)
        try {
            const response = await pastOrderApi.preview(customerId, files)
            if (response.success) {
                setPreview(response.preview)
                const sel = {}
                response.preview.rows.forEach((r, i) => {
                    sel[i] = r.status !== 'error'
                })
                setSelectedRows(sel)
                setExpandedRows({})
            } else {
                toast.error(response.message || 'Preview failed')
            }
        } catch (err) {
            toast.error(err?.message || 'Network error')
        } finally {
            setPreviewing(false)
        }
    }

    async function handleCommit() {
        if (!preview) return
        const rowsToImport = preview.rows.filter((_, i) => selectedRows[i])
        if (rowsToImport.length === 0) {
            toast.error('Nothing selected to import')
            return
        }

        const modeLabel = importMode === 'historical'
            ? 'as completed/picked up'
            : 'as pending payment'

        if (!confirm(`Import ${rowsToImport.length} order${rowsToImport.length === 1 ? '' : 's'} ${modeLabel}? This cannot be undone.`)) return

        setCommitting(true)
        try {
            const response = await pastOrderApi.commit(Number(customerId), rowsToImport, importMode)
            if (response.success) {
                setResult(response.result)
                toast.success(`Imported ${response.result.imported} order${response.result.imported === 1 ? '' : 's'}`)
                setFiles([])
                setPreview(null)
                setSelectedRows({})
                setExpandedRows({})
            } else {
                toast.error(response.message || 'Import failed')
            }
        } catch (err) {
            toast.error(err?.message || 'Network error')
        } finally {
            setCommitting(false)
        }
    }

    // ─── Item-level editing helpers ─────────────────────────────────────────

    function updateItem(rowIdx, itemIdx, patch) {
        setPreview(prev => {
            const next = { ...prev, rows: prev.rows.slice() }
            const row = { ...next.rows[rowIdx] }
            const items = row.items.slice()
            items[itemIdx] = { ...items[itemIdx], ...patch }
            row.items = items
            row.lineCount = items.length
            row.total = items.reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.quantity) || 0), 0)
            next.rows[rowIdx] = row
            return next
        })
    }

    function deleteItem(rowIdx, itemIdx) {
        setPreview(prev => {
            const next = { ...prev, rows: prev.rows.slice() }
            const row = { ...next.rows[rowIdx] }
            row.items = row.items.filter((_, i) => i !== itemIdx)
            row.lineCount = row.items.length
            row.total = row.items.reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.quantity) || 0), 0)
            next.rows[rowIdx] = row
            return next
        })
    }

    function addItem(rowIdx) {
        setPreview(prev => {
            const next = { ...prev, rows: prev.rows.slice() }
            const row = { ...next.rows[rowIdx] }
            row.items = [
                ...row.items,
                {
                    itemNumber: '',
                    description: '',
                    year: null,
                    color: null,
                    cost: 0,
                    quantity: 1,
                    inventoryId: null,
                    category: null,
                    medallionType: null,
                },
            ]
            row.lineCount = row.items.length
            next.rows[rowIdx] = row
            return next
        })
    }

    // Auto re-match against inventory when item_number changes
    function handleItemNumberChange(rowIdx, itemIdx, newItemNumber) {
        const match = inventoryByItemNumber.get(newItemNumber.trim().toUpperCase())
        if (match) {
            updateItem(rowIdx, itemIdx, {
                itemNumber: newItemNumber,
                inventoryId: match.id,
                category: match.category,
                medallionType: match.medallion_type,
            })
        } else {
            updateItem(rowIdx, itemIdx, {
                itemNumber: newItemNumber,
                inventoryId: null,
                category: null,
                medallionType: null,
            })
        }
    }

    // When the user picks an item from the dropdown, fill all fields except cost
    function handleInventoryPick(rowIdx, itemIdx, inventoryRowId) {
        if (!inventoryRowId) {
            updateItem(rowIdx, itemIdx, {
                inventoryId: null,
                category: null,
                medallionType: null,
            })
            return
        }
        const inv = inventory.find(x => String(x.id) === String(inventoryRowId))
        if (!inv) return
        updateItem(rowIdx, itemIdx, {
            inventoryId: inv.id,
            itemNumber: inv.item_number,
            description: inv.description,
            year: inv.year,
            color: inv.color,
            category: inv.category,
            medallionType: inv.medallion_type,
            // cost intentionally NOT changed — preserve PDF historical price
        })
    }

    if (!isSysAdmin) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <p className="text-gray-600">Sysadmin access required.</p>
            </div>
        )
    }

    const selectedCount = Object.values(selectedRows).filter(Boolean).length

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Import Past Orders</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Temporary tool for backfilling orders from old Anvil PDF receipts.
                    Pick a home group, choose a mode, drop in its PDFs, review the preview,
                    expand any row to edit the parsed items, and import.
                </p>
            </div>

            {/* Step 1 — Customer + mode */}
            <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Home group
                    </label>
                    <select
                        value={customerId}
                        onChange={(e) => { setCustomerId(e.target.value); setPreview(null); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="">— Pick a home group —</option>
                        {customers.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Import as
                    </label>
                    <select
                        value={importMode}
                        onChange={(e) => { setImportMode(e.target.value); setPreview(null); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="historical">Historical — already paid &amp; picked up</option>
                        <option value="active">Active — needs payment &amp; packing</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        {importMode === 'historical'
                            ? 'Orders will be marked picked_up with paid/packed/pickup dates set to the order date.'
                            : 'Orders will be marked pending_payment so they show up in the normal pay → pack → pickup workflow.'}
                    </p>
                </div>
            </div>

            {/* Step 2 — File selection */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                    PDFs ({files.length} selected)
                </label>
                <input
                    type="file"
                    multiple
                    accept="application/pdf,.pdf"
                    onChange={(e) => handleFiles(e.target.files)}
                    className="block w-full text-sm text-gray-600
                        file:mr-3 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-medium
                        file:bg-ghasc-navy file:text-white
                        hover:file:bg-ghasc-navy-dark cursor-pointer"
                />
                {files.length > 0 && (
                    <div className="text-xs text-gray-500">
                        {files.slice(0, 5).map(f => f.name).join(', ')}
                        {files.length > 5 ? ` and ${files.length - 5} more` : ''}
                    </div>
                )}
                <Button
                    onClick={handlePreview}
                    loading={previewing}
                    disabled={!customerId || files.length === 0}
                >
                    Preview {files.length > 0 ? `(${files.length} files)` : ''}
                </Button>
            </div>

            {/* Step 3 — Preview table */}
            {preview && (
                <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                    <div className="flex flex-wrap gap-4 text-sm">
                        <span><strong>{preview.totalFiles}</strong> total</span>
                        <span className="text-ghasc-success"><strong>{preview.ready}</strong> ready</span>
                        <span className="text-amber-700"><strong>{preview.warnings}</strong> warnings</span>
                        <span className="text-ghasc-danger"><strong>{preview.errors}</strong> errors</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="px-3 py-2 text-left w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedCount === preview.rows.filter(r => r.status !== 'error').length && selectedCount > 0}
                                            onChange={(e) => {
                                                const next = {}
                                                preview.rows.forEach((r, i) => {
                                                    next[i] = e.target.checked && r.status !== 'error'
                                                })
                                                setSelectedRows(next)
                                            }}
                                        />
                                    </th>
                                    <th className="px-3 py-2 text-left w-10"></th>
                                    <th className="px-3 py-2 text-left">File</th>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Submitter</th>
                                    <th className="px-3 py-2 text-left">Home Group (PDF)</th>
                                    <th className="px-3 py-2 text-right">Items</th>
                                    <th className="px-3 py-2 text-right">Total</th>
                                    <th className="px-3 py-2 text-left">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.rows.map((row, i) => (
                                    <PreviewRow
                                        key={i}
                                        row={row}
                                        rowIdx={i}
                                        selected={!!selectedRows[i]}
                                        expanded={!!expandedRows[i]}
                                        onSelectChange={(v) => setSelectedRows(s => ({ ...s, [i]: v }))}
                                        onToggleExpand={() => setExpandedRows(s => ({ ...s, [i]: !s[i] }))}
                                        inventory={inventory}
                                        onItemNumberChange={(itemIdx, val) => handleItemNumberChange(i, itemIdx, val)}
                                        onItemChange={(itemIdx, patch) => updateItem(i, itemIdx, patch)}
                                        onItemDelete={(itemIdx) => deleteItem(i, itemIdx)}
                                        onItemAdd={() => addItem(i)}
                                        onInventoryPick={(itemIdx, invId) => handleInventoryPick(i, itemIdx, invId)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="text-sm text-gray-600">
                            {selectedCount} of {preview.rows.length} selected to import
                        </span>
                        <Button
                            onClick={handleCommit}
                            loading={committing}
                            disabled={selectedCount === 0}
                        >
                            Import {selectedCount > 0 ? `${selectedCount} order${selectedCount === 1 ? '' : 's'}` : ''}
                        </Button>
                    </div>
                </div>
            )}

            {/* Result summary */}
            {result && (
                <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
                    <h3 className="font-semibold text-ghasc-navy">Import result</h3>
                    <p className="text-sm">
                        <span className="text-ghasc-success font-medium">{result.imported}</span> imported,{' '}
                        <span className="text-gray-600">{result.skipped}</span> skipped
                    </p>
                    {result.errors?.length > 0 && (
                        <div className="bg-red-50 rounded p-3 text-sm">
                            <div className="font-medium text-ghasc-danger mb-1">Errors:</div>
                            <ul className="list-disc pl-5 text-ghasc-danger">
                                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// ───────────────────────────────────────────────────────────────────────────
//  Preview row + its inline expansion panel for editing items
// ───────────────────────────────────────────────────────────────────────────

function PreviewRow({
    row,
    rowIdx,
    selected,
    expanded,
    onSelectChange,
    onToggleExpand,
    inventory,
    onItemNumberChange,
    onItemChange,
    onItemDelete,
    onItemAdd,
    onInventoryPick,
}) {
    return (
        <>
            <tr className="border-t border-gray-100">
                <td className="px-3 py-2">
                    <input
                        type="checkbox"
                        checked={selected}
                        disabled={row.status === 'error'}
                        onChange={(e) => onSelectChange(e.target.checked)}
                    />
                </td>
                <td className="px-3 py-2">
                    <button
                        type="button"
                        onClick={onToggleExpand}
                        className="text-ghasc-navy hover:text-ghasc-navy-dark text-lg leading-none"
                        title={expanded ? 'Collapse' : 'Expand'}
                    >
                        {expanded ? '▼' : '▶'}
                    </button>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{row.fileName}</td>
                <td className="px-3 py-2">
                    {row.orderDate
                        ? new Date(row.orderDate).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                        })
                        : <span className="text-ghasc-danger">—</span>}
                </td>
                <td className="px-3 py-2">{row.submitterName || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-gray-600">{row.homeGroupOnPdf || <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-right">{row.lineCount}</td>
                <td className="px-3 py-2 text-right">${Number(row.total || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs">
                    <StatusBadge status={row.status} />
                    {row.errorMessage && (
                        <div className="text-ghasc-danger mt-1">{row.errorMessage}</div>
                    )}
                    {row.messages?.map((m, mi) => (
                        <div key={mi} className="text-amber-700 mt-1">{m}</div>
                    ))}
                </td>
            </tr>
            {expanded && (
                <tr className="bg-gray-50">
                    <td colSpan={9} className="px-6 py-4">
                        <ItemsEditor
                            items={row.items || []}
                            inventory={inventory}
                            onItemNumberChange={onItemNumberChange}
                            onItemChange={onItemChange}
                            onItemDelete={onItemDelete}
                            onItemAdd={onItemAdd}
                            onInventoryPick={onInventoryPick}
                        />
                    </td>
                </tr>
            )}
        </>
    )
}

function ItemsEditor({
    items,
    inventory,
    onItemNumberChange,
    onItemChange,
    onItemDelete,
    onItemAdd,
    onInventoryPick,
}) {
    return (
        <div className="space-y-2">
            <table className="w-full text-xs">
                <thead className="text-gray-600 uppercase tracking-wider">
                    <tr>
                        <th className="px-2 py-1 text-left">Pick from inventory</th>
                        <th className="px-2 py-1 text-left">Item #</th>
                        <th className="px-2 py-1 text-left">Description</th>
                        <th className="px-2 py-1 text-left">Year</th>
                        <th className="px-2 py-1 text-left">Color</th>
                        <th className="px-2 py-1 text-right">Cost</th>
                        <th className="px-2 py-1 text-right">Qty</th>
                        <th className="px-2 py-1 text-right">Subtotal</th>
                        <th className="px-2 py-1"></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((it, j) => (
                        <tr key={j} className="border-t border-gray-200">
                            <td className="px-2 py-1">
                                <select
                                    value={it.inventoryId || ''}
                                    onChange={(e) => onInventoryPick(j, e.target.value)}
                                    className="w-full text-xs px-1 py-0.5 border border-gray-300 rounded"
                                >
                                    <option value="">— pick —</option>
                                    {inventory.map(inv => (
                                        <option key={inv.id} value={inv.id}>
                                            {inv.item_number} — {inv.description}
                                            {inv.year ? ` (${inv.year})` : ''}
                                            {inv.color ? ` ${inv.color}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    type="text"
                                    value={it.itemNumber || ''}
                                    onChange={(e) => onItemNumberChange(j, e.target.value)}
                                    className={`w-24 text-xs px-1 py-0.5 border rounded ${it.inventoryId ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    type="text"
                                    value={it.description || ''}
                                    onChange={(e) => onItemChange(j, { description: e.target.value })}
                                    className="w-full text-xs px-1 py-0.5 border border-gray-300 rounded"
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    type="text"
                                    value={it.year || ''}
                                    onChange={(e) => onItemChange(j, { year: e.target.value })}
                                    className="w-20 text-xs px-1 py-0.5 border border-gray-300 rounded"
                                />
                            </td>
                            <td className="px-2 py-1">
                                <input
                                    type="text"
                                    value={it.color || ''}
                                    onChange={(e) => onItemChange(j, { color: e.target.value })}
                                    className="w-24 text-xs px-1 py-0.5 border border-gray-300 rounded"
                                />
                            </td>
                            <td className="px-2 py-1 text-right">
                                <input
                                    type="number"
                                    step="0.01"
                                    value={it.cost ?? 0}
                                    onChange={(e) => onItemChange(j, { cost: Number(e.target.value) })}
                                    className="w-16 text-xs px-1 py-0.5 border border-gray-300 rounded text-right"
                                />
                            </td>
                            <td className="px-2 py-1 text-right">
                                <input
                                    type="number"
                                    value={it.quantity ?? 0}
                                    onChange={(e) => onItemChange(j, { quantity: Number(e.target.value) })}
                                    className="w-14 text-xs px-1 py-0.5 border border-gray-300 rounded text-right"
                                />
                            </td>
                            <td className="px-2 py-1 text-right font-medium">
                                ${(Number(it.cost) * Number(it.quantity)).toFixed(2)}
                            </td>
                            <td className="px-2 py-1 text-right">
                                <button
                                    type="button"
                                    onClick={() => onItemDelete(j)}
                                    className="text-ghasc-danger hover:text-red-700 text-sm"
                                    title="Delete this item"
                                >
                                    ✕
                                </button>
                            </td>
                        </tr>
                    ))}
                    {items.length === 0 && (
                        <tr><td colSpan={9} className="text-center text-gray-400 py-2">No items.</td></tr>
                    )}
                </tbody>
            </table>
            <div className="flex justify-between items-center pt-2">
                <button
                    type="button"
                    onClick={onItemAdd}
                    className="text-xs text-ghasc-navy hover:text-ghasc-navy-dark"
                >
                    + Add item
                </button>
                <div className="text-xs text-gray-500">
                    Green outline = matches inventory. Amber = doesn't match (will import with NULL inventory_id).
                </div>
            </div>
        </div>
    )
}

function StatusBadge({ status }) {
    const styles = {
        ready: 'bg-green-100 text-green-800',
        warning: 'bg-amber-100 text-amber-800',
        error: 'bg-red-100 text-red-800',
    }
    return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
        </span>
    )
}